import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import { RootState } from "_shared/_stores/root-reducer";
import { AppDispatch } from "_shared/_stores/store-types";
import UserEntityMemberInvitationSchema, {
  UserEntityMemberInvitationJSON,
} from "legal-entities/invitations/member-invitations/email-based/_schema/user-entity-member-invitation-schema";
import { userEntityMemberInvitationApi } from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";

/** The Ably event the API publishes when somebody accepts an invitation. */
export const INVITATION_ACCEPTED_EVENT = "entity-member-invitation-accepted";

/**
 * Keeps a pending-invitations table honest without a reload.
 *
 * Accepting a member invitation used to be completely silent: the person who sent it found out by
 * reloading a table, which for a team leader who has just entered twelve colleagues means reloading
 * it twelve times.
 *
 * The event carries the invitation in the same shape the invitations endpoint returns, so it is
 * validated against the same schema a refetch is. What it then does is invalidate that entity's
 * list rather than patch a row into the cache: the list query is what decides which invitations are
 * still "active", and a row patched in by hand would be this middleware's opinion of that rule
 * rather than the server's. Invalidating keeps realtime exactly equivalent to refreshing, which is
 * what the working agreements ask for, and RTK keeps the current rows on screen while it refetches.
 */
const handleInvitationAccepted = (payload: unknown) => (dispatch: AppDispatch) => {
  const invitation: UserEntityMemberInvitationJSON = UserEntityMemberInvitationSchema.validateJSON(payload);

  dispatch(
    userEntityMemberInvitationApi.util.invalidateTags([
      { type: "UserEntityMemberInvitation" as const, id: `LegalEntity-${invitation.legalEntityId}` },
    ]),
  );
};

const callbacks = withCallbacks<RootState, AppDispatch>().add(INVITATION_ACCEPTED_EVENT, handleInvitationAccepted);

export const entityMemberInvitationsAblyReduxMiddleware = reduxAblyMiddleware<RootState, AppDispatch>({
  callbacks,
  channelName: ablyChannelNamesForSession.entityMemberInvitations,
  connectionHealth: { key: "entityMemberInvitations", label: "Member Invitations" },
  shouldConnectionStartImmediately: true,
});
